import { AuthService } from './auth.service';
import { IdleTracker, IdleTrackerOptions } from './utils/idle';

type OIDCUserInfoExtension = {
  exp: number; // expiration time
  iat: number; // issue time
};

const hasExpirations = (
  info: AuthService['userInfo'],
): info is AuthService['userInfo'] & OIDCUserInfoExtension => {
  return !!info && 'exp' in info && 'iat' in info;
};

export class IdleRefresh {
  private idleTracker: IdleTracker | undefined;
  private isRefreshing = false;
  private expiringNotificationsTimeInSeconds = 60;

  constructor(private userManager: AuthService) {
    this.bootstrap();
  }
  private bootstrap() {
    const options: IdleTrackerOptions = {
      onIdle: () => {
        this.onIdle();
      },
      onActive: () => {
        this.onActive();
      },
      onHide: () => {
        this.onHide();
      },
      onShow: () => {
        this.onShow();
      },
      keepTracking: true,
      idleTime: this.getTokenTTL(),
    };
    this.idleTracker = new IdleTracker(options);
    this.expiringNotificationsTimeInSeconds =
      this.userManager.userManager?.settings
        ?.accessTokenExpiringNotificationTimeInSeconds || 60; //same default from oidc client ts
    this.userManager.userManager?.events?.addAccessTokenExpiring(
      this.handleTokenExpiry,
    );

    this.idleTracker.start();
  }
  private async handleRefresh(force = false) {
    if (this.idleTracker?.isIdle === false && !this.isRefreshing) {
      // avoid refreshing in parallel
      this.isRefreshing = true;
      await this.userManager.getAccessToken?.(force);
      this.isRefreshing = false;
    }
  }
  private handleTokenExpiry = async () => {
    await this.handleRefresh(true);
    if (this.getTokenTTL() <= this.expiringNotificationsTimeInSeconds * 1000) {
      // it means that we are reaching max session TTL, and user will be logged out
      this.userManager.userManager?.events?.removeAccessTokenExpiring(
        this.handleTokenExpiry,
      );
    }
  };
  private async onActive() {
    // user became active, refresh token
    await this.handleRefresh();
  }
  private async onIdle() {}
  private async onHide() {}
  private async onShow() {
    // tab is visible again, try to refresh token
    await this.handleRefresh();
  }

  private getTokenTTL() {
    if (hasExpirations(this.userManager.userInfo)) {
      return (
        (this.userManager.userInfo.exp - this.userManager.userInfo.iat) * 1000
      );
    }
    return 300 * 1000; //default 5 minutes
  }
}
